import { useContext, useEffect, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { FormikProps } from 'formik';
import { TranslationContext } from 'contexts/translation-context';
import { detectUserCountry } from 'utils/geolocation-helper';
import {
  getRapaportUserData,
  isUserDataForCurrentEnv,
  matchCompanyType
} from 'utils/cookie-user-data';
import type { CompanyType, JoinFormType } from 'types/join';
import { fetchCompanyTypes } from 'components/join/actions/index';
import { getPlanPeriod } from 'components/join/components/formats';

type UseRegistrationPrefillArgs = {
  form: FormikProps<JoinFormType>;
  cacheForm: JoinFormType | null;
  companyTypes: CompanyType[];
};

/**
 * Owns all registration form prefill logic, sourced from either URL query
 * params (today's flow) or the `rapaport_user_data` cookie dropped by the
 * pre-registration site.
 *
 * Precedence: when the URL carries a `plan` param, the cookie is ignored
 * entirely (URL-only flow) — unless the URL also carries `use_detail_ck=true`,
 * in which case personal details always come from the cookie, and the plan
 * comes from the cookie when it has one (falling back to the URL's
 * plan/service/period otherwise). Without a URL plan the cookie supplies
 * plan/service/period and personal details. A mismatched-environment cookie is
 * ignored, and an in-progress cached form takes priority over the cookie.
 */
export const useRegistrationPrefill = ({
  form,
  cacheForm,
  companyTypes
}: UseRegistrationPrefillArgs) => {
  const TRANSLATION_TEXT = useContext(TranslationContext) as { [key: string]: string };
  const dispatch = useDispatch();

  const userData = useMemo(() => {
    // When the URL carries a plan, use the URL flow exclusively: ignore the
    // cookie for plan, personal details, company type, and the HubSpot skip.
    // `use_detail_ck=true` overrides this and keeps the cookie in play even
    // when the URL carries a plan.
    const urlParams = new URLSearchParams(window.location.search);
    const urlPlan = urlParams.get('plan');
    const useDetailsFromCookie = urlParams.get('use_detail_ck') === 'true';
    if (urlPlan && !useDetailsFromCookie) return null;

    const raw = getRapaportUserData();
    return raw; // && isUserDataForCurrentEnv(raw) ? raw : null;
  }, []);

  // True when the user arrived via a valid, environment-matched cookie. The
  // pre-registration site already submitted a HubSpot form for these leads, so
  // callers use this to avoid a duplicate registration-side HubSpot submission.
  const isFromCookie = Boolean(userData);

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const extractedService = urlParams.get('service');
    const extractedPlan = urlParams.get('plan');
    const extractedPlanPeriod = urlParams.get('planperiod');

    // The cookie's plan wins when present; the URL's plan (with its service
    // and period) is only the fallback for when the cookie has no plan.
    const cookiePlan = userData?.preferred_plan || '';
    const planFromCookie = Boolean(cookiePlan);
    const rawPlan = cookiePlan || extractedPlan || '';
    const formatedPlan = rawPlan.replace(/[_ ]/g, '');
    const formatedPlanPeriod = getPlanPeriod(
      TRANSLATION_TEXT,
      planFromCookie ? '' : extractedPlanPeriod || ''
    );
    // The pre-registration cookie only ever carries trade plans, so the
    // cookie-sourced service is always "Rapaport Trade".
    const service = planFromCookie
      ? 'Rapaport Trade'
      : extractedService ?? (userData ? 'Rapaport Trade' : 'Rapaport');

    // Personal details only come from the cookie, and only when the user has no
    // in-progress (cached) registration that should take priority.
    const shouldPrefillFromCookie = Boolean(userData) && !cacheForm?.email;
    const cookieCountry = shouldPrefillFromCookie ? userData?.country ?? null : null;

    form.setValues({
      ...form.values,
      service,
      initialPlan: formatedPlan,
      initialPlanPeriod: formatedPlanPeriod,
      ...(shouldPrefillFromCookie
        ? {
            firstName: userData?.firstname ?? form.values.firstName,
            lastName: userData?.lastname ?? form.values.lastName,
            email: userData?.email ?? form.values.email,
            companyName: userData?.company ?? form.values.companyName,
            companyCountry: cookieCountry ?? form.values.companyCountry,
            cellPhone: userData?.phone ?? form.values.cellPhone
          }
        : {})
    });

    dispatch(fetchCompanyTypes());

    // Detect user's country and set it if not already set (skip when the cookie
    // or an India plan already determines the country).
    const detectAndSetCountry = async () => {
      if (!form.values.companyCountry && !cookieCountry && !rawPlan.includes('india')) {
        const detectedCountry = await detectUserCountry();
        if (detectedCountry && form.values.companyCountry === null) {
          form.setFieldValue('companyCountry', detectedCountry);
        }
      }
    };
    detectAndSetCountry();
  }, []);

  // Company types load asynchronously, so match the cookie's company type once
  // the options are available and the field has not already been set.
  useEffect(() => {
    if (!userData || cacheForm?.email) return;
    if (!companyTypes?.length || form.values.companyType) return;

    const cookieCompanyType = userData.hubspot_company_type?.[0] ?? '';
    const matched = matchCompanyType(cookieCompanyType, companyTypes);
    if (matched) {
      form.setFieldValue('companyType', matched);
    }
  }, [companyTypes, userData]);

  return { isFromCookie };
};
